/**
 * Chart palette for the SDPD briefing.
 *
 * Continuous with the palette used on the TrialFindings report so the two
 * pages read as one system, with one deliberate change: the "interaction
 * audio" violet is stepped from #7c3aed to #6d28d9. Against the primary blue
 * the original pair separated by only ΔE 7.7 under deuteranopia, which sits in
 * the marginal 6–8 band; #6d28d9 clears it at ΔE 8.5 while staying visually
 * the same violet.
 *
 * Validated with the dataviz palette validator, all-pairs, light mode:
 *   lightness band PASS · chroma floor PASS · CVD separation PASS
 *   normal-vision floor PASS · contrast WARN on accent + good
 *
 * The contrast warning is why every chart in this directory carries visible
 * value labels rather than relying on fill color alone. Do not remove the
 * direct labels.
 */

export const C = {
  /** Categorical slot 1: primary. Also used for narrative dictation. */
  primary: "#1a78c2",
  /** Categorical slot 2: interaction and body-worn audio. */
  interaction: "#6d28d9",
  /** Categorical slot 3: accent, used for highlight and the "after" state. */
  accent: "#f97316",
  /** Categorical slot 4: positive outcome, time saved. */
  good: "#10b981",

  primarySoft: "#dbeafe",
  goodSoft: "#d1fae5",
  amber: "#f59e0b",
  danger: "#dc2626",

  /** The "before" / manual state. Deliberately neutral, never a hue. */
  manual: "#cbd5e1",
  manualDeep: "#94a3b8",

  text: "#475569",
  textDim: "#94a3b8",
  ink: "#0f172a",
  gridline: "rgba(148, 163, 184, 0.25)",
  surface: "#ffffff",
} as const;

/** Fixed categorical order. Never cycle past slot 4; fold into "Other". */
export const CATEGORICAL = [C.primary, C.interaction, C.accent, C.good] as const;

export function fmtInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

export function fmtMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

export function fmtMoneyFull(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

export function fmtHours(n: number): string {
  return `${Math.round(n).toLocaleString("en-US")} hr`;
}

/** "1 hr 45 min" from a minute count. */
export function fmtDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}
